import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Settings, CreditCard, Bell, Loader2, Save, Smartphone, Building2, 
  Wallet, Banknote, AlertCircle, Clock, FileText, CheckCircle,
  Info, Send, Mail, Calendar, Copy, Key, ScrollText, Shield, RotateCcw,
  Globe, Link2, ExternalLink, Crown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import LandlordSubscription from './LandlordSubscription';

const PAYMENT_METHODS = [
  { 
    key: 'gcash', 
    name: 'GCash', 
    icon: Smartphone, 
    color: 'bg-blue-500',
    description: 'Mobile wallet payment via GCash'
  },
  { 
    key: 'maya', 
    name: 'Maya (PayMaya)', 
    icon: Smartphone, 
    color: 'bg-green-500',
    description: 'Mobile wallet payment via Maya'
  },
  { 
    key: 'diskartech', 
    name: 'Diskartech', 
    icon: Smartphone, 
    color: 'bg-orange-500',
    description: 'RCBC Diskartech mobile banking'
  },
  { 
    key: 'palawan', 
    name: 'Palawan Express', 
    icon: Building2, 
    color: 'bg-yellow-500',
    description: 'Palawan Pawnshop remittance'
  },
  { 
    key: 'cash', 
    name: 'Cash Payment', 
    icon: Banknote, 
    color: 'bg-emerald-500',
    description: 'In-person cash payment'
  },
];

export default function LandlordSettings() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Lease Terms state
  const [leaseTerms, setLeaseTerms] = useState({
    house_rules: '',
    payment_terms: '',
    terms_of_service: ''
  });
  const [savingTerms, setSavingTerms] = useState(false);
  const [resettingTerms, setResettingTerms] = useState(false);
  const [termsTab, setTermsTab] = useState('house_rules');
  
  // Payment Schedule state
  const [paymentSchedule, setPaymentSchedule] = useState({
    schedule_type: 'fixed',
    fixed_day: 5,
    window_start_day: 1,
    window_end_day: 5,
    bimonthly_first_day: 1,
    bimonthly_second_day: 15
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const copyLandlordCode = () => {
    if (user?.landlord_code) {
      navigator.clipboard.writeText(user.landlord_code);
      toast.success(t('common.copied') + '!');
    }
  };
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadSettings();
    loadLeaseTerms();
    // Refresh user data to get latest landlord_code if it was added after login
    refreshUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const loadSettings = async () => {
    try {
      const response = await landlordAPI.getSettings();
      setSettings(response.data);
      // Load payment schedule if exists
      if (response.data.payment_schedule) {
        setPaymentSchedule(response.data.payment_schedule);
      }
      // Load timezone
      setSelectedTimezone(response.data.timezone || localStorage.getItem('u_dorm_timezone') || browserTimezone);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await landlordAPI.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentSchedule = async () => {
    setSavingSchedule(true);
    try {
      await landlordAPI.updateSettings({ payment_schedule: paymentSchedule });
      toast.success('Payment schedule saved successfully');
    } catch (error) {
      console.error('Error saving payment schedule:', error);
      toast.error('Failed to save payment schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveTimezone = async (tz) => {
    setSavingTimezone(true);
    setSelectedTimezone(tz);
    try {
      await landlordAPI.updateSettings({ timezone: tz });
      localStorage.setItem('u_dorm_timezone', tz);
      toast.success(`Timezone set to ${tz}`);
    } catch (error) {
      toast.error('Failed to save timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await landlordAPI.sendTestEmail();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email. Please check your email configuration.');
    } finally {
      setSendingTest(false);
    }
  };

  const updatePaymentMethod = (method, field, value) => {
    setSettings(prev => ({
      ...prev,
      payment_methods: {
        ...prev.payment_methods,
        [method]: {
          ...prev.payment_methods?.[method],
          [field]: value
        }
      }
    }));
  };

  const updateBankAccount = (field, value) => {
    setSettings(prev => ({
      ...prev,
      payment_methods: {
        ...prev.payment_methods,
        bank: {
          ...prev.payment_methods?.bank,
          [field]: value
        }
      }
    }));
  };

  const updateNotification = (field, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: value
      }
    }));
  };

  // Lease Terms functions
  const loadLeaseTerms = async () => {
    try {
      const response = await landlordAPI.getLeaseTerms();
      setLeaseTerms(response.data);
    } catch (error) {
      console.error('Error loading lease terms:', error);
    }
  };

  const handleSaveTerms = async () => {
    setSavingTerms(true);
    try {
      await landlordAPI.updateLeaseTerms(leaseTerms);
      toast.success(t('settings.leaseTermsSaved'));
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setSavingTerms(false);
    }
  };

  const handleResetTerms = async () => {
    if (!confirm(t('settings.confirmResetTerms'))) {
      return;
    }
    setResettingTerms(true);
    try {
      const response = await landlordAPI.resetLeaseTerms();
      setLeaseTerms(response.data.lease_terms);
      toast.success(t('settings.leaseTermsReset'));
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setResettingTerms(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-2">
            <Settings className="w-7 h-7 text-[#C85A42]" />
            {t('nav.settings')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('settings.configurePaymentMethods')}</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className={`bg-[#C85A42] hover:bg-[#B04A35] ${activeTab === 'subscription' ? 'hidden' : ''}`}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {t('settings.saveSettings')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="general" className="flex items-center gap-2" data-testid="tab-general">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">General</span>
          </TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-2" data-testid="tab-payment">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Payment</span>
          </TabsTrigger>
          <TabsTrigger value="lease-terms" className="flex items-center gap-2" data-testid="tab-lease-terms">
            <ScrollText className="w-4 h-4" />
            <span className="hidden sm:inline">Lease Terms</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2" data-testid="tab-subscription">
            <Crown className="w-4 h-4" />
            <span className="hidden sm:inline">Subscription</span>
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          {/* Landlord Code */}
          {user?.landlord_code && (
            <Card className="border-[#C85A42]/30 bg-[#C85A42]/5 dark:bg-[#C85A42]/10" data-testid="landlord-code-card">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#C85A42]/20">
                    <Key className="w-5 h-5 text-[#C85A42]" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Your Landlord Code</p>
                    <p className="text-sm text-muted-foreground">Share this code with tenants to simplify registration</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xl font-mono font-bold tracking-widest bg-background px-4 py-2 rounded-lg border">
                    {user.landlord_code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyLandlordCode}
                    data-testid="copy-landlord-code-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#C85A42] flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Timezone</CardTitle>
                  <CardDescription>
                    All dates and times across the app will display in this timezone
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p>Your browser detected timezone: <strong>{browserTimezone}</strong></p>
                    <p className="text-blue-600 dark:text-blue-300 mt-1">This was auto-set on your first login. Change it below if your property is in a different timezone.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Property Timezone</Label>
                <Select
                  value={selectedTimezone}
                  onValueChange={handleSaveTimezone}
                  data-testid="timezone-select"
                >
                  <SelectTrigger className="w-full max-w-md" data-testid="timezone-select-trigger">
                    <SelectValue placeholder="Select timezone..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="Pacific/Honolulu">Hawaii (UTC-10)</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska (UTC-9)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time (UTC-8)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (UTC-7)</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (UTC-6)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time (UTC-5)</SelectItem>
                    <SelectItem value="America/Sao_Paulo">Brasilia (UTC-3)</SelectItem>
                    <SelectItem value="Europe/London">London (UTC+0)</SelectItem>
                    <SelectItem value="Europe/Paris">Central Europe (UTC+1)</SelectItem>
                    <SelectItem value="Europe/Athens">Eastern Europe (UTC+2)</SelectItem>
                    <SelectItem value="Asia/Dubai">Dubai (UTC+4)</SelectItem>
                    <SelectItem value="Asia/Kolkata">India (UTC+5:30)</SelectItem>
                    <SelectItem value="Asia/Bangkok">Bangkok (UTC+7)</SelectItem>
                    <SelectItem value="Asia/Singapore">Singapore (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Manila">Philippines (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Shanghai">China (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Japan (UTC+9)</SelectItem>
                    <SelectItem value="Asia/Seoul">Korea (UTC+9)</SelectItem>
                    <SelectItem value="Australia/Sydney">Sydney (UTC+11)</SelectItem>
                    <SelectItem value="Pacific/Auckland">New Zealand (UTC+13)</SelectItem>
                  </SelectContent>
                </Select>
                {savingTimezone && <p className="text-xs text-muted-foreground">Saving...</p>}
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Current time in {selectedTimezone || browserTimezone}:</strong>{' '}
                  {new Date().toLocaleString('en-PH', { 
                    timeZone: selectedTimezone || browserTimezone,
                    year: 'numeric', month: 'short', day: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Methods Tab */}
        <TabsContent value="payment" className="space-y-6">
          {/* Payment Info Visibility Banner */}
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800" data-testid="payment-info-visibility-card">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">{t('settings.paymentInfoVisibility')}</p>
                <p className="text-blue-700 dark:text-blue-300">{t('settings.paymentInfoVisibilityDesc')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Wallets & Cash */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {PAYMENT_METHODS.map((method) => {
              const Icon = method.icon;
              const isEnabled = settings?.payment_methods?.[method.key]?.enabled || false;
              
              return (
                <Card key={method.key} className={`transition-all ${isEnabled ? 'ring-2 ring-[#C85A42]/20' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${method.color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{method.name}</CardTitle>
                          <CardDescription className="text-xs">{method.description}</CardDescription>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => updatePaymentMethod(method.key, 'enabled', checked)}
                        data-testid={`switch-${method.key}`}
                      />
                    </div>
                  </CardHeader>
                  
                  {isEnabled && (
                    <CardContent className="space-y-4 pt-0">
                      <Separator />
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`${method.key}-name`} className="text-sm">Account Name</Label>
                          <Input
                            id={`${method.key}-name`}
                            placeholder="e.g., Juan Dela Cruz"
                            value={settings?.payment_methods?.[method.key]?.account_name || ''}
                            onChange={(e) => updatePaymentMethod(method.key, 'account_name', e.target.value)}
                            data-testid={`input-${method.key}-name`}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`${method.key}-number`} className="text-sm">
                            {method.key === 'cash' ? 'Contact Number' : 'Account Number'}
                          </Label>
                          <Input
                            id={`${method.key}-number`}
                            placeholder={method.key === 'cash' ? 'e.g., +63 912 345 6789' : 'e.g., 09171234567'}
                            value={settings?.payment_methods?.[method.key]?.account_number || ''}
                            onChange={(e) => updatePaymentMethod(method.key, 'account_number', e.target.value)}
                            data-testid={`input-${method.key}-number`}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`${method.key}-instructions`} className="text-sm">
                            {method.key === 'cash' ? 'Payment Location/Instructions' : 'Additional Instructions'}
                          </Label>
                          <Textarea
                            id={`${method.key}-instructions`}
                            placeholder={method.key === 'cash' 
                              ? 'e.g., Pay at the admin office, Ground Floor, Mon-Fri 9AM-5PM' 
                              : 'e.g., Please include your unit number in the message'}
                            value={settings?.payment_methods?.[method.key]?.instructions || ''}
                            onChange={(e) => updatePaymentMethod(method.key, 'instructions', e.target.value)}
                            rows={2}
                            data-testid={`input-${method.key}-instructions`}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`${method.key}-link`} className="text-sm flex items-center gap-1.5">
                            <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
                            Online Payment Link <span className="text-muted-foreground font-normal">(optional)</span>
                          </Label>
                          <Input
                            id={`${method.key}-link`}
                            type="url"
                            placeholder="e.g., https://gcash.com/pay/your-link"
                            value={settings?.payment_methods?.[method.key]?.payment_link || ''}
                            onChange={(e) => updatePaymentMethod(method.key, 'payment_link', e.target.value)}
                            data-testid={`input-${method.key}-link`}
                          />
                          <p className="text-xs text-muted-foreground">Tenants will see a "Pay Online" button that opens this link.</p>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Bank Account */}
          <Card className={`transition-all ${settings?.payment_methods?.bank?.enabled ? 'ring-2 ring-[#C85A42]/20' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Bank Transfer</CardTitle>
                    <CardDescription className="text-xs">Direct bank deposit or online transfer</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings?.payment_methods?.bank?.enabled || false}
                  onCheckedChange={(checked) => updateBankAccount('enabled', checked)}
                  data-testid="switch-bank"
                />
              </div>
            </CardHeader>
            
            {settings?.payment_methods?.bank?.enabled && (
              <CardContent className="space-y-4 pt-0">
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="bank-name" className="text-sm">Bank Name</Label>
                    <Input
                      id="bank-name"
                      placeholder="e.g., BDO, BPI, Metrobank"
                      value={settings?.payment_methods?.bank?.bank_name || ''}
                      onChange={(e) => updateBankAccount('bank_name', e.target.value)}
                      data-testid="input-bank-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bank-branch" className="text-sm">Branch</Label>
                    <Input
                      id="bank-branch"
                      placeholder="e.g., Makati Main Branch"
                      value={settings?.payment_methods?.bank?.branch || ''}
                      onChange={(e) => updateBankAccount('branch', e.target.value)}
                      data-testid="input-bank-branch"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bank-account-name" className="text-sm">Account Name</Label>
                    <Input
                      id="bank-account-name"
                      placeholder="e.g., Juan Dela Cruz"
                      value={settings?.payment_methods?.bank?.account_name || ''}
                      onChange={(e) => updateBankAccount('account_name', e.target.value)}
                      data-testid="input-bank-account-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bank-account-number" className="text-sm">Account Number</Label>
                    <Input
                      id="bank-account-number"
                      placeholder="e.g., 1234-5678-9012"
                      value={settings?.payment_methods?.bank?.account_number || ''}
                      onChange={(e) => updateBankAccount('account_number', e.target.value)}
                      data-testid="input-bank-account-number"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bank-link" className="text-sm flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
                    Online Payment Link <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="bank-link"
                    type="url"
                    placeholder="e.g., https://your-bank.com/transfer-link"
                    value={settings?.payment_methods?.bank?.payment_link || ''}
                    onChange={(e) => updateBankAccount('payment_link', e.target.value)}
                    data-testid="input-bank-link"
                  />
                  <p className="text-xs text-muted-foreground">Tenants will see a "Pay Online" button that opens this link.</p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* General Online Payment Link */}
          <Card className={`transition-all ${settings?.payment_methods?.online_payment_link?.enabled ? 'ring-2 ring-[#C85A42]/20' : ''}`} data-testid="online-payment-link-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#C85A42] flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Online Payment Link</CardTitle>
                    <CardDescription className="text-xs">A general payment page (Stripe, PayPal.me, or any URL) tenants can pay through</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings?.payment_methods?.online_payment_link?.enabled || false}
                  onCheckedChange={(checked) => updatePaymentMethod('online_payment_link', 'enabled', checked)}
                  data-testid="switch-online-payment-link"
                />
              </div>
            </CardHeader>

            {settings?.payment_methods?.online_payment_link?.enabled && (
              <CardContent className="space-y-4 pt-0">
                <Separator />
                <div className="space-y-1.5">
                  <Label htmlFor="online-link-url" className="text-sm">Payment Link URL</Label>
                  <Input
                    id="online-link-url"
                    type="url"
                    placeholder="e.g., https://buy.stripe.com/your-link"
                    value={settings?.payment_methods?.online_payment_link?.url || ''}
                    onChange={(e) => updatePaymentMethod('online_payment_link', 'url', e.target.value)}
                    data-testid="input-online-link-url"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="online-link-instructions" className="text-sm">Instructions (optional)</Label>
                  <Textarea
                    id="online-link-instructions"
                    placeholder="e.g., Pay your rent online securely. Please include your unit number as the reference."
                    value={settings?.payment_methods?.online_payment_link?.instructions || ''}
                    onChange={(e) => updatePaymentMethod('online_payment_link', 'instructions', e.target.value)}
                    rows={2}
                    data-testid="input-online-link-instructions"
                  />
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Lease Terms Tab */}
        <TabsContent value="lease-terms" className="space-y-6">
          {/* Header with Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg flex-1">
              <div className="flex items-start gap-3">
                <ScrollText className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">Lease Agreement Terms</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    These terms will automatically be included in all new lease agreements. Tenants must review and agree before acknowledging their lease.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleResetTerms}
                disabled={resettingTerms}
                data-testid="reset-terms-btn"
              >
                {resettingTerms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Reset
              </Button>
              <Button
                onClick={handleSaveTerms}
                disabled={savingTerms}
                className="bg-[#C85A42] hover:bg-[#B04A35]"
                data-testid="save-terms-btn"
              >
                {savingTerms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Terms
              </Button>
            </div>
          </div>

          {/* Lease Terms Content */}
          <Card>
            <CardContent className="p-6">
              <Tabs value={termsTab} onValueChange={setTermsTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="house_rules" className="flex items-center gap-2" data-testid="house-rules-tab">
                    <Shield className="w-4 h-4" />
                    <span className="hidden sm:inline">House Rules</span>
                  </TabsTrigger>
                  <TabsTrigger value="payment_terms" className="flex items-center gap-2" data-testid="payment-terms-tab">
                    <CreditCard className="w-4 h-4" />
                    <span className="hidden sm:inline">Payment Terms</span>
                  </TabsTrigger>
                  <TabsTrigger value="terms_of_service" className="flex items-center gap-2" data-testid="tos-tab">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Terms of Service</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="house_rules" className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg mb-4">
                    <h3 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      House Rules and Regulations
                    </h3>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                      Define the rules that tenants must follow while residing in your property.
                    </p>
                  </div>
                  <Textarea
                    value={leaseTerms.house_rules || ''}
                    onChange={(e) => setLeaseTerms({ ...leaseTerms, house_rules: e.target.value })}
                    placeholder="Enter house rules and regulations..."
                    className="min-h-[300px] font-mono text-sm"
                    data-testid="house-rules-textarea"
                  />
                </TabsContent>

                <TabsContent value="payment_terms" className="space-y-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg mb-4">
                    <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Payment Terms
                    </h3>
                    <p className="text-emerald-700 dark:text-emerald-300 text-sm mt-1">
                      Specify payment due dates, accepted methods, late fees, and other payment-related policies.
                    </p>
                  </div>
                  <Textarea
                    value={leaseTerms.payment_terms || ''}
                    onChange={(e) => setLeaseTerms({ ...leaseTerms, payment_terms: e.target.value })}
                    placeholder="Enter payment terms..."
                    className="min-h-[300px] font-mono text-sm"
                    data-testid="payment-terms-textarea"
                  />
                </TabsContent>

                <TabsContent value="terms_of_service" className="space-y-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg mb-4">
                    <h3 className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Terms of Service
                    </h3>
                    <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                      Additional terms and conditions that govern the lease agreement.
                    </p>
                  </div>
                  <Textarea
                    value={leaseTerms.terms_of_service || ''}
                    onChange={(e) => setLeaseTerms({ ...leaseTerms, terms_of_service: e.target.value })}
                    placeholder="Enter terms of service..."
                    className="min-h-[300px] font-mono text-sm"
                    data-testid="tos-textarea"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="border-muted">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> Changes to these terms will only apply to new leases created after saving.
                Existing leases will retain their original terms.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          {/* Email Service Info */}
          <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950 dark:to-indigo-950 border-purple-200 dark:border-purple-800">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-purple-900 dark:text-purple-100">Automated Email Notifications</p>
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      Email notifications are sent automatically based on your settings below. 
                      The system checks daily at 8 AM for reminders to send.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                  className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900"
                  data-testid="send-test-email-btn"
                >
                  {sendingTest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Test Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Enhanced Payment Schedule Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Payment Due Date Schedule</CardTitle>
                    <CardDescription>Configure when rent payments are due</CardDescription>
                  </div>
                </div>
                <Button
                  onClick={handleSavePaymentSchedule}
                  disabled={savingSchedule}
                  size="sm"
                  className="bg-[#C85A42] hover:bg-[#B04A35]"
                  data-testid="save-schedule-btn"
                >
                  {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Schedule Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Schedule Type</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Fixed Day Option */}
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentSchedule.schedule_type === 'fixed' 
                        ? 'border-[#C85A42] bg-[#C85A42]/5 ring-2 ring-[#C85A42]/20' 
                        : 'border-border hover:border-[#C85A42]/50'
                    }`}
                    onClick={() => setPaymentSchedule(prev => ({ ...prev, schedule_type: 'fixed' }))}
                    data-testid="schedule-type-fixed"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentSchedule.schedule_type === 'fixed' ? 'border-[#C85A42]' : 'border-muted-foreground'
                      }`}>
                        {paymentSchedule.schedule_type === 'fixed' && (
                          <div className="w-2 h-2 rounded-full bg-[#C85A42]" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">Fixed Monthly Date</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">
                      Rent is due on a specific day each month
                    </p>
                  </div>

                  {/* Payment Window Option */}
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentSchedule.schedule_type === 'window' 
                        ? 'border-[#C85A42] bg-[#C85A42]/5 ring-2 ring-[#C85A42]/20' 
                        : 'border-border hover:border-[#C85A42]/50'
                    }`}
                    onClick={() => setPaymentSchedule(prev => ({ ...prev, schedule_type: 'window' }))}
                    data-testid="schedule-type-window"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentSchedule.schedule_type === 'window' ? 'border-[#C85A42]' : 'border-muted-foreground'
                      }`}>
                        {paymentSchedule.schedule_type === 'window' && (
                          <div className="w-2 h-2 rounded-full bg-[#C85A42]" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">Payment Window</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">
                      Rent can be paid within a date range
                    </p>
                  </div>

                  {/* Bi-monthly Option */}
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentSchedule.schedule_type === 'bimonthly' 
                        ? 'border-[#C85A42] bg-[#C85A42]/5 ring-2 ring-[#C85A42]/20' 
                        : 'border-border hover:border-[#C85A42]/50'
                    }`}
                    onClick={() => setPaymentSchedule(prev => ({ ...prev, schedule_type: 'bimonthly' }))}
                    data-testid="schedule-type-bimonthly"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentSchedule.schedule_type === 'bimonthly' ? 'border-[#C85A42]' : 'border-muted-foreground'
                      }`}>
                        {paymentSchedule.schedule_type === 'bimonthly' && (
                          <div className="w-2 h-2 rounded-full bg-[#C85A42]" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">Bi-Monthly</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">
                      Rent is due twice per month on specific days
                    </p>
                  </div>

                  {/* Per Lease Option */}
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      paymentSchedule.schedule_type === 'per_lease' 
                        ? 'border-[#C85A42] bg-[#C85A42]/5 ring-2 ring-[#C85A42]/20' 
                        : 'border-border hover:border-[#C85A42]/50'
                    }`}
                    onClick={() => setPaymentSchedule(prev => ({ ...prev, schedule_type: 'per_lease' }))}
                    data-testid="schedule-type-per-lease"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentSchedule.schedule_type === 'per_lease' ? 'border-[#C85A42]' : 'border-muted-foreground'
                      }`}>
                        {paymentSchedule.schedule_type === 'per_lease' && (
                          <div className="w-2 h-2 rounded-full bg-[#C85A42]" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">Per Lease Agreement</span>
                    </div>
                    <p className="text-sm text-muted-foreground ml-6">
                      Due date is set individually in each lease
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Configuration based on selected type */}
              {paymentSchedule.schedule_type === 'fixed' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <p className="text-sm text-emerald-800 dark:text-emerald-200">
                      <strong>Fixed Day:</strong> Rent is due on the <strong>{paymentSchedule.fixed_day || 5}th</strong> of each month.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="fixed-day" className="text-sm">Due Date (day of month)</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input
                        id="fixed-day"
                        type="number"
                        min="1"
                        max="28"
                        value={paymentSchedule.fixed_day || 5}
                        onChange={(e) => setPaymentSchedule(prev => ({ ...prev, fixed_day: parseInt(e.target.value) || 5 }))}
                        className="w-24"
                        data-testid="input-fixed-day"
                      />
                      <span className="text-sm text-muted-foreground">of each month</span>
                    </div>
                  </div>
                </div>
              )}

              {paymentSchedule.schedule_type === 'window' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <p className="text-sm text-emerald-800 dark:text-emerald-200">
                      <strong>Payment Window:</strong> Tenants can pay from the <strong>{paymentSchedule.window_start_day || 1}st</strong> to the <strong>{paymentSchedule.window_end_day || 5}th</strong> of each month.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="window-start" className="text-sm">Window Start</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          id="window-start"
                          type="number"
                          min="1"
                          max="28"
                          value={paymentSchedule.window_start_day || 1}
                          onChange={(e) => setPaymentSchedule(prev => ({ ...prev, window_start_day: parseInt(e.target.value) || 1 }))}
                          className="w-20"
                          data-testid="input-window-start"
                        />
                        <span className="text-sm text-muted-foreground">of month</span>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="window-end" className="text-sm">Window End (Deadline)</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          id="window-end"
                          type="number"
                          min="1"
                          max="28"
                          value={paymentSchedule.window_end_day || 5}
                          onChange={(e) => setPaymentSchedule(prev => ({ ...prev, window_end_day: parseInt(e.target.value) || 5 }))}
                          className="w-20"
                          data-testid="input-window-end"
                        />
                        <span className="text-sm text-muted-foreground">of month</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {paymentSchedule.schedule_type === 'bimonthly' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <p className="text-sm text-emerald-800 dark:text-emerald-200">
                      <strong>Bi-Monthly:</strong> Rent is due on the <strong>{paymentSchedule.bimonthly_first_day || 1}st</strong> and <strong>{paymentSchedule.bimonthly_second_day || 15}th</strong> of each month.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="bimonthly-first" className="text-sm">First Due Date</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          id="bimonthly-first"
                          type="number"
                          min="1"
                          max="15"
                          value={paymentSchedule.bimonthly_first_day || 1}
                          onChange={(e) => setPaymentSchedule(prev => ({ ...prev, bimonthly_first_day: parseInt(e.target.value) || 1 }))}
                          className="w-20"
                          data-testid="input-bimonthly-first"
                        />
                        <span className="text-sm text-muted-foreground">of month</span>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bimonthly-second" className="text-sm">Second Due Date</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          id="bimonthly-second"
                          type="number"
                          min="16"
                          max="28"
                          value={paymentSchedule.bimonthly_second_day || 15}
                          onChange={(e) => setPaymentSchedule(prev => ({ ...prev, bimonthly_second_day: parseInt(e.target.value) || 15 }))}
                          className="w-20"
                          data-testid="input-bimonthly-second"
                        />
                        <span className="text-sm text-muted-foreground">of month</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {paymentSchedule.schedule_type === 'per_lease' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">Per Lease Due Dates</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        When this option is selected, the payment due date will be determined by each individual lease agreement. 
                        You can set the due date when creating or editing a lease.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Late payment notifications and fees will be applied based on the schedule you configure.
              </p>
            </CardContent>
          </Card>

          {/* Rent Reminders */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Rent Payment Reminders</CardTitle>
                  <CardDescription>Notify tenants before rent is due</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable rent reminders</p>
                  <p className="text-sm text-muted-foreground">Send notifications before due date</p>
                </div>
                <Switch
                  checked={settings?.notifications?.rent_reminder_enabled || false}
                  onCheckedChange={(checked) => updateNotification('rent_reminder_enabled', checked)}
                  data-testid="switch-rent-reminder"
                />
              </div>
              
              {settings?.notifications?.rent_reminder_enabled && (
                <div className="pt-2">
                  <Label htmlFor="rent-days" className="text-sm">Days before due date</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      id="rent-days"
                      type="text"
                      min="1"
                      max="30"
                      value={settings?.notifications?.rent_reminder_days_before || 5}
                      onChange={(e) => updateNotification('rent_reminder_days_before', parseInt(e.target.value) || 5)}
                      className="w-24"
                      data-testid="input-rent-days"
                    />
                    <span className="text-sm text-muted-foreground">days before rent is due</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Late Payment Notifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Late Payment Notifications</CardTitle>
                  <CardDescription>Alert tenants about overdue payments</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable late payment alerts</p>
                  <p className="text-sm text-muted-foreground">Send notifications after due date passes</p>
                </div>
                <Switch
                  checked={settings?.notifications?.late_payment_notification || false}
                  onCheckedChange={(checked) => updateNotification('late_payment_notification', checked)}
                  data-testid="switch-late-notification"
                />
              </div>
              
              {settings?.notifications?.late_payment_notification && (
                <div className="pt-2">
                  <Label htmlFor="late-days" className="text-sm">Days after due date</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      id="late-days"
                      type="text"
                      min="1"
                      max="30"
                      value={settings?.notifications?.late_payment_days_after || 3}
                      onChange={(e) => updateNotification('late_payment_days_after', parseInt(e.target.value) || 3)}
                      className="w-24"
                      data-testid="input-late-days"
                    />
                    <span className="text-sm text-muted-foreground">days after rent is overdue</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Late Payment Fees */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Late Payment Fees</CardTitle>
                  <CardDescription>Charge penalties for overdue payments</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable late payment fees</p>
                  <p className="text-sm text-muted-foreground">Automatically calculate late fees</p>
                </div>
                <Switch
                  checked={settings?.notifications?.late_payment_fee_enabled || false}
                  onCheckedChange={(checked) => updateNotification('late_payment_fee_enabled', checked)}
                  data-testid="switch-late-fee"
                />
              </div>
              
              {settings?.notifications?.late_payment_fee_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="fee-amount" className="text-sm">Fixed Fee Amount (₱)</Label>
                    <Input
                      id="fee-amount"
                      type="text"
                      min="0"
                      placeholder="e.g., 500"
                      value={settings?.notifications?.late_payment_fee_amount || ''}
                      onChange={(e) => updateNotification('late_payment_fee_amount', parseFloat(e.target.value) || 0)}
                      data-testid="input-fee-amount"
                    />
                    <p className="text-xs text-muted-foreground">One-time fixed late fee</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fee-percentage" className="text-sm">Percentage Fee (%)</Label>
                    <Input
                      id="fee-percentage"
                      type="text"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="e.g., 5"
                      value={settings?.notifications?.late_payment_fee_percentage || ''}
                      onChange={(e) => updateNotification('late_payment_fee_percentage', parseFloat(e.target.value) || 0)}
                      data-testid="input-fee-percentage"
                    />
                    <p className="text-xs text-muted-foreground">Percentage of monthly rent</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lease Acknowledgment */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Lease Acknowledgment Reminders</CardTitle>
                  <CardDescription>Remind tenants to acknowledge their lease</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable acknowledgment reminders</p>
                  <p className="text-sm text-muted-foreground">Notify tenants with pending lease acknowledgment</p>
                </div>
                <Switch
                  checked={settings?.notifications?.lease_acknowledgment_reminder || false}
                  onCheckedChange={(checked) => updateNotification('lease_acknowledgment_reminder', checked)}
                  data-testid="switch-lease-reminder"
                />
              </div>
              
              {settings?.notifications?.lease_acknowledgment_reminder && (
                <div className="pt-2">
                  <Label htmlFor="ack-days" className="text-sm">Days after lease creation</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      id="ack-days"
                      type="text"
                      min="1"
                      max="14"
                      value={settings?.notifications?.lease_acknowledgment_reminder_days || 3}
                      onChange={(e) => updateNotification('lease_acknowledgment_reminder_days', parseInt(e.target.value) || 3)}
                      className="w-24"
                      data-testid="input-ack-days"
                    />
                    <span className="text-sm text-muted-foreground">days after lease is created</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-6" data-testid="tab-content-subscription">
          <LandlordSubscription />
        </TabsContent>
      </Tabs>

      {/* Bottom Save Button (mobile) */}
      <div className={`sm:hidden ${activeTab === 'subscription' ? 'hidden' : ''}`}>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full bg-[#C85A42] hover:bg-[#B04A35]"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
